import { Info, Trophy } from 'lucide-react';
import { PLACEMENT_SCORING } from '@fq/common/src/pickem-placement';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  getRoundPoints,
  getTotalRounds,
  resolveRoundLabel,
} from '../lib/pickem-scoring';

type Props = {
  /**
   * Per-round override stored on the event. When `null`/`undefined` the
   * component shows the shared default scoring curve.
   */
  pointsPerRound: Record<string, number> | null | undefined;
  /**
   * The number of competitors at the start of the bracket (used to compute
   * how many rounds to display).
   */
  startingRound: number;
  /**
   * Bracket format — drives the finals explanation. Double-elim events score
   * the Grand Final on the winner + reset + score (opponent ignored), whereas
   * single-elim finals require predicting the exact matchup.
   */
  bracketFormat?: string;
  placementScoring?: boolean;
};

/**
 * Modal that explains scoring for an event. Lists each round's points and
 * spells out the finals delta-scoring rule so users understand why their
 * loser-score prediction matters.
 *
 * Uses Dialog instead of Popover — Popover positioning was unreliable inside
 * the page's nested flex/absolute layouts and would occasionally render
 * off-screen or fail to open.
 */
export function PickemScoringInfo({
  pointsPerRound,
  startingRound,
  bracketFormat,
  placementScoring,
}: Props) {
  const [open, setOpen] = useState(false);
  const totalRounds = getTotalRounds(startingRound);
  const rounds = Array.from({ length: totalRounds }, (_, i) => i + 1);
  const isDoubleElim = !!bracketFormat?.startsWith('double_elimination');

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-1.5 h-8 px-2"
        >
          <Info className="h-3.5 w-3.5" />
          <span className="text-xs">How scoring works</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>How scoring works</DialogTitle>
          <DialogDescription>
            Earn points for each match you correctly predict. Later rounds carry
            more weight — and the final has its own bonus rule.
          </DialogDescription>
        </DialogHeader>
        {placementScoring && (
          <section className="space-y-2">
            <h3 className="font-semibold">
              Placement points — added to bracket points
            </h3>
            <p className="text-sm text-muted-foreground">
              Each player in your starting lineup earns points once for their
              confirmed final placing, regardless of seed or bracket slot. Top-8
              qualification is already included. Outside the top 64 earns zero.
            </p>
            <dl className="grid grid-cols-2 gap-1 text-sm">
              {PLACEMENT_SCORING.map((band) => (
                <div
                  key={band.from}
                  className="flex justify-between gap-3 border-b py-1"
                >
                  <dt>
                    {band.from === band.to
                      ? band.from
                      : `${band.from}–${band.to}`}
                  </dt>
                  <dd>{band.points} pts</dd>
                </div>
              ))}
            </dl>
          </section>
        )}
        <div className="space-y-4 pt-2">
          <div>
            <h4 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">
              Points per round
            </h4>
            <div className="border rounded-md divide-y divide-border/60">
              {rounds.map((r) => {
                const label = resolveRoundLabel(r, totalRounds);
                const pts = getRoundPoints(r, pointsPerRound);
                return (
                  <div
                    key={r}
                    className="flex items-center justify-between px-3 py-2 text-sm"
                  >
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-mono font-semibold tabular-nums">
                      {pts} pt{pts === 1 ? '' : 's'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <Trophy className="h-4 w-4 text-amber-400 flex-shrink-0" />
              <h4 className="text-sm font-semibold">
                {isDoubleElim ? 'Grand Final bonus' : 'Finals bonus'}
              </h4>
            </div>
            {isDoubleElim ? (
              <>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Pick the{' '}
                  <span className="font-semibold text-foreground">
                    champion
                  </span>{' '}
                  to earn the Grand Final round points — the opponent they faced
                  doesn&rsquo;t matter. Then earn a flat bonus when you also
                  nail{' '}
                  <span className="font-semibold text-foreground">both</span>{' '}
                  the bracket reset and the exact final-set score:
                </p>
                <ul className="mt-2 space-y-0.5 text-xs font-mono text-muted-foreground">
                  <li>
                    <span className="text-amber-400 font-bold">+5 pts</span> —
                    correct reset + exact final-set score
                  </li>
                  <li>
                    <span className="text-amber-400 font-bold">+0 pts</span> —
                    either the reset or the score is off
                  </li>
                </ul>
                <p className="mt-2 text-xs text-muted-foreground">
                  The reset on its own is worth nothing — it only unlocks the
                  bonus alongside the score.
                </p>
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  You must predict the{' '}
                  <span className="font-semibold text-foreground">
                    exact matchup
                  </span>{' '}
                  — both finalists, not just the eventual winner. When the
                  matchup is right, you earn bonus points{' '}
                  <span className="font-semibold text-foreground">
                    on top of
                  </span>{' '}
                  the finals round points, based on how close your predicted
                  loser score was to the real one:
                </p>
                <ul className="mt-2 space-y-0.5 text-xs font-mono text-muted-foreground">
                  <li>
                    <span className="text-amber-400 font-bold">5 pts</span> —
                    exact score
                  </li>
                  <li>
                    <span className="text-amber-400 font-bold">4 pts</span> — 1
                    off
                  </li>
                  <li>
                    <span className="text-amber-400 font-bold">3 pts</span> — 2
                    off
                  </li>
                  <li>
                    <span className="text-amber-400 font-bold">2 pts</span> — 3
                    off
                  </li>
                  <li>
                    <span className="text-amber-400 font-bold">1 pt</span> — 4
                    off
                  </li>
                </ul>
                <p className="mt-2 text-xs text-muted-foreground">
                  Wrong finalists or wrong winner ={' '}
                  <span className="font-semibold">0 pts</span>, regardless of
                  the predicted score.
                </p>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
